import { deserializeHash } from "@formant/core/common/serializeHash";

export interface ILocationLike {
    pathname: string;
    search: string;
    hash?: string;
}

/**
 * Pages that serve something other than the signed-in organization's own use
 * of the app: sign-in and account recovery, public share links, third-party
 * OAuth callbacks, and Formant staff tools. Coherence has no equivalent for
 * them, so they always stay on app.formant.
 */
const exemptPathPrefixes = [
    "/login",
    "/sso-login",
    "/forgot-password",
    "/resend-invitation",
    "/impersonate",
    "/shares",
    "/preview",
    "/share-teleop",
    "/preview-teleop",
    "/integrations",
    "/internal",
    "/admin-panel",
    "/terms",
    "/privacy-policy",
];

/**
 * A login handoff to another app (`redirectUrl`, used by Platform and others)
 * and an explicit sign-out have to finish on app.formant.
 */
const exemptSearchParams = ["redirectUrl", "logout"];

export const useLegacyParam = "useLegacy";

export function isCoherenceRedirectExempt({
    pathname,
    search,
}: ILocationLike): boolean {
    const isExemptPath = exemptPathPrefixes.some(
        prefix => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );
    if (isExemptPath) {
        return true;
    }
    const params = new URLSearchParams(search);
    return exemptSearchParams.some(name => params.has(name));
}

/**
 * `?useLegacy=true` keeps the tab on app.formant; any other value clears that
 * choice. Returns undefined when the parameter is absent.
 */
export function legacyPreferenceFromSearch(
    search: string,
): boolean | undefined {
    const value = new URLSearchParams(search).get(useLegacyParam);
    if (value === null) {
        return undefined;
    }
    return value === "true";
}

const devicePagePattern =
    /^\/devices\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i;

function eventParamsFromHash(hash: string): URLSearchParams {
    const params = new URLSearchParams();
    let data: { selectedEventId?: unknown; time?: unknown } | null;
    try {
        data = deserializeHash(hash.replace(/^#/, "")) as typeof data;
    } catch {
        return params;
    }
    if (typeof data?.selectedEventId === "string") {
        params.set("eventId", data.selectedEventId);
    }
    const time =
        typeof data?.time === "string" ? new Date(data.time) : undefined;
    if (time && !isNaN(time.getTime())) {
        params.set("time", time.toISOString());
    }
    return params;
}

/**
 * Where a redirected user lands in Coherence. A device page opens the same
 * device, along with the event and time in its hash, which is how alert links
 * (admin-api's generateEventUrl) point at an event. Every other page opens
 * Coherence's home.
 */
export function coherenceUrlFor(
    coherenceUrl: string,
    { pathname, hash = "" }: ILocationLike,
): string {
    const deviceId = devicePagePattern.exec(pathname)?.[1];
    if (!deviceId) {
        return coherenceUrl;
    }
    const url = new URL(`/dashboard/device/${deviceId}`, coherenceUrl);
    url.search = eventParamsFromHash(hash).toString();
    return url.toString();
}

/**
 * The feature-flags service sits behind Vercel's bot checkpoint, which rejects
 * cross-origin requests from app.formant. Platform proxies the service under
 * `/feature-flags` on its own host, so the flags are read through Platform.
 */
export function featureFlagsUrl(platformUrl: string): string {
    return new URL("/feature-flags/api/flags", platformUrl).toString();
}

export async function fetchCoherenceRedirectEnabled(
    platformUrl: string,
    token: string,
    fetchFn: typeof fetch = fetch,
): Promise<boolean> {
    const response = await fetchFn(featureFlagsUrl(platformUrl), {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
        return false;
    }
    const flags: unknown = await response.json();
    return (
        typeof flags === "object" &&
        flags !== null &&
        (flags as { coherenceRedirectEnabled?: unknown })
            .coherenceRedirectEnabled === true
    );
}

/**
 * Decides whether to send the user to Coherence. Any failure to read the flag
 * keeps the user on app.formant, so the redirect can never lock anyone out.
 */
export async function shouldRedirectToCoherence({
    locations,
    keepLegacy,
    readFlag,
}: {
    locations: ILocationLike[];
    keepLegacy: boolean;
    readFlag: () => Promise<boolean>;
}): Promise<boolean> {
    if (keepLegacy || locations.some(isCoherenceRedirectExempt)) {
        return false;
    }
    try {
        return await readFlag();
    } catch {
        return false;
    }
}
